import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Car, FileText, Link2, LogOut, Megaphone, Package, PlaySquare, SlidersHorizontal, Store, Tags, UserCog, ChevronsUpDown } from 'lucide-react';
import { api } from '@/lib/api';
import { AccessManager } from '@/components/AccessManager';
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
} from '@/components/ui/sidebar';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const NAV = [
	{
		title: 'Reklamy',
		to: '/prebid',
		icon: Megaphone,
		match: (p: string) => p.startsWith('/prebid'),
	},
	{
		title: 'Przekierowania',
		to: '/redirects',
		icon: Link2,
		match: (p: string) => p.startsWith('/redirects'),
	},
	{
		title: 'Produkty',
		to: '/feed',
		icon: Package,
		match: (p: string) => p === '/feed',
		children: [
			{
				title: 'Mapowanie tagów',
				to: '/feed/tags',
				icon: Tags,
				match: (p: string) => p.startsWith('/feed/tags'),
			},
			{
				title: 'Moto',
				to: '/feed/moto',
				icon: Car,
				match: (p: string) => p.startsWith('/feed/moto'),
			},
		],
	},
	{
		title: 'Buy-boxy',
		to: '/buybox',
		icon: Store,
		match: (p: string) => p.startsWith('/buybox'),
	},
	{
		title: 'Player wideo',
		to: '/video',
		icon: PlaySquare,
		match: (p: string) => p.startsWith('/video'),
	},
	{
		title: 'ads.txt',
		to: '/adstxt',
		icon: FileText,
		match: (p: string) => p.startsWith('/adstxt'),
	},
];

export function AppSidebar() {
	const location = useLocation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const me = useQuery({ queryKey: ['me'], queryFn: api.me });
	const [accessOpen, setAccessOpen] = useState(false);

	const email = me.data?.email ?? '';
	const initials = email ? email.slice(0, 2).toUpperCase() : 'CRM';

	async function logout() {
		await api.logout();
		queryClient.clear();
		navigate('/login', { replace: true });
	}

	return (
		<>
			<Sidebar collapsible="icon">
				<SidebarHeader>
					<SidebarMenu>
						<SidebarMenuItem>
							<SidebarMenuButton size="lg" asChild>
								<Link to="/prebid">
									<div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
										<SlidersHorizontal className="size-4" />
									</div>
									<div className="grid flex-1 text-left text-sm leading-tight">
										<span className="truncate font-semibold">Mini CRM</span>
										<span className="truncate text-xs text-muted-foreground">Spiders Web</span>
									</div>
								</Link>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarHeader>

				<SidebarContent>
					<SidebarGroup>
						<SidebarGroupLabel>Moduły</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								{NAV.map((item) => (
										<SidebarMenuItem key={item.to}>
											<SidebarMenuButton asChild isActive={item.match(location.pathname)} tooltip={item.title}>
												<Link to={item.to}>
													<item.icon />
													<span>{item.title}</span>
												</Link>
											</SidebarMenuButton>
											{item.children?.length ? (
												<SidebarMenuSub>
													{item.children.map((child) => (
														<SidebarMenuSubItem key={child.to}>
															<SidebarMenuSubButton asChild isActive={child.match(location.pathname)}>
																<Link to={child.to}>
																	<child.icon />
																	<span>{child.title}</span>
																</Link>
															</SidebarMenuSubButton>
														</SidebarMenuSubItem>
													))}
												</SidebarMenuSub>
											) : null}
										</SidebarMenuItem>
								))}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				</SidebarContent>

				<SidebarFooter>
					<SidebarMenu>
						<SidebarMenuItem>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<SidebarMenuButton
										size="lg"
										className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
									>
										<Avatar className="size-8 rounded-lg">
											<AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
										</Avatar>
										<div className="grid flex-1 text-left text-sm leading-tight">
											<span className="truncate font-medium">{email || 'Zalogowany'}</span>
										</div>
										<ChevronsUpDown className="ml-auto size-4" />
									</SidebarMenuButton>
								</DropdownMenuTrigger>
								<DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg" side="top" align="start">
									<DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">{email}</DropdownMenuLabel>
									<DropdownMenuSeparator />
									<DropdownMenuItem onClick={() => setAccessOpen(true)}>
										<UserCog className="size-4" />
										Dostępy
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem onClick={logout}>
										<LogOut className="size-4" />
										Wyloguj
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarFooter>
				<SidebarRail />
			</Sidebar>

			<AccessManager open={accessOpen} onOpenChange={setAccessOpen} />
		</>
	);
}
